import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions, type SecurePermissions } from "@webuild-ai/react-auth";

import { logAccessAttempt } from "@/services/accessAuditLog";

export type PermissionKey = keyof SecurePermissions;

interface ProtectedRouteProps {
  /**
   * The permission required to access this route
   */
  requiredPermission: PermissionKey;
  /**
   * The component to render if authorised
   */
  children: React.ReactNode;
  /**
   * Optional redirect path when unauthorised (defaults to /access-denied)
   */
  redirectTo?: string;
}

/**
 * ProtectedRoute component that guards routes based on user permissions.
 *
 * This component:
 * - Checks if the user has the required permission
 * - Logs access attempts for audit purposes
 * - Redirects unauthorised users to the access denied page
 *
 * Uses the already-loaded permissions from AuthUserProvider context,
 * so there is no additional latency from API calls.
 *
 * @example
 * <ProtectedRoute requiredPermission="monitoring">
 *   <MetricsPage />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredPermission,
  children,
  redirectTo = "/access-denied",
}) => {
  const { permissions, isLoading } = usePermissions();
  const location = useLocation();

  const hasPermission = permissions[requiredPermission] || false;

  // Log the access attempt only when route, permission, or access status changes
  useEffect(() => {
    if (!isLoading) {
      logAccessAttempt({
        route: location.pathname,
        permission: requiredPermission,
        granted: hasPermission,
      });
    }
  }, [location.pathname, requiredPermission, hasPermission, isLoading]);

  // While permissions are loading, render nothing (PrivateRoute handles loading state)
  if (isLoading) {
    return null;
  }

  if (!hasPermission) {
    // Redirect to safe page with state indicating access was denied
    return (
      <Navigate
        replace
        state={{
          accessDenied: true,
          attemptedRoute: location.pathname,
          requiredPermission,
        }}
        to={redirectTo}
      />
    );
  }

  return <>{children}</>;
};

/**
 * Route permission configuration for easy reference and maintenance.
 * Maps route paths to their required permissions.
 */
export const routePermissions: Record<string, PermissionKey> = {
  "/workflows": "workflows",
  "/datahub": "knowledge_hub",
  "/prompts": "prompt_library",
  "/metrics": "monitoring",
};

/**
 * Helper to get the required permission for a given route
 */
export const getRoutePermission = (path: string): PermissionKey | null => {
  // Check exact match first
  if (routePermissions[path]) {
    return routePermissions[path];
  }

  // Check if path starts with any protected route
  for (const [route, permission] of Object.entries(routePermissions)) {
    if (path.startsWith(route)) {
      return permission;
    }
  }

  return null;
};
